import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import {Dialog,DialogTitle,DialogContent,DialogActions,IconButton,Typography,Box,Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./InvoiceDeleteDialog.css";

export default function InvoiceDeleteDialog({
  open,
  onClose,
  record,
  onDeleted,
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const rawRecord = useMemo(() => record ?? null, [record]);

  /*
    De fiecare dată când se deschide dialogul:
    - resetăm loading-ul
    - resetăm eventualele erori vechi
   */
  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  /*
    Face efectiv ștergerea facturii.
    Dacă merge bine, apelăm callback-ul onDeleted.
    Dacă nu merge, afișăm un mesaj de eroare prietenos.
   */
  const handleDelete = async () => {
    if (!rawRecord?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/invoices-register/${rawRecord.id}/`);
      onDeleted?.();
    } catch (e) {
      setError(
        "Cannot delete this invoice because it has dependencies in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="invoice-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header-ul dialogului apare doar cât timp nu avem eroare */}
      {!error && (
        <DialogTitle className="id-header">
          <Box className="id-header-content">
            <Box className="id-title-section">
              <WarningAmberIcon className="id-warning-icon" />
              <Typography variant="h5" component="span" className="id-title">
                Delete Invoice
              </Typography>
            </Box>

            {/* Butonul de închidere */}
            <IconButton
              onClick={onClose}
              className="id-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="id-content">
        {error ? (
          //Dacă ștergerea a eșuat, afișăm un mesaj clar de eroare.
          <Alert
            severity="error"
            className="id-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Invoice
            </Typography>
            <Typography variant="body2" className="id-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          // Dacă nu există eroare, afișăm mesajul de confirmare și consecințele ștergerii.

          <Box className="id-confirm-content">
            {rawRecord?.invoice_number && (
              <Box className="id-number-container">
                <Typography variant="subtitle2" className="id-number-label">
                  Invoice Number
                </Typography>
                <Typography variant="h6" className="id-number-value">
                  {rawRecord.invoice_number}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" className="id-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="id-consequences">
              <Typography variant="body2" className="id-consequence-item">
                • The invoice will be permanently deleted from the system
              </Typography>
              <Typography variant="body2" className="id-consequence-item">
                • All associated invoice data will be removed
              </Typography>
              <Typography variant="body2" className="id-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="id-actions">
        {error ? (
          //Dacă a apărut o eroare, utilizatorul primește doar un buton simplu de închidere.
          <Button
            onClick={onClose}
            variant="contained"
            className="id-btn id-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            {/* Închide dialogul fără să șteargă */}
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="id-btn id-btn-cancel"
            >
              Cancel
            </Button>

            {/* Confirmă ștergerea */}
            <Button
              onClick={handleDelete}
              disabled={deleting || !rawRecord?.id}
              variant="contained"
              className="id-btn id-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}